import React, { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { toast } from 'react-toastify'
import {
    Box,
    Button,
    Checkbox,
    Dialog,
    FormControlLabel,
    IconButton,
    TextField,
    Typography,
} from '@mui/material'
import {
    CloseRounded,
    DownloadRounded,
    PictureAsPdfRounded,
    ImageRounded,
} from '@mui/icons-material'
import { VideoNotesAnalysisState } from '../../../../features/types'
import { videonotesApiSlice } from '../../../../features/section/videonotesSlice'
import { capitalize } from '../../../../utils/functions'
import { useHeadScope } from '../../../head/headScope'
import { ui } from '../../../../utils/uiTokens'

interface Props {
    open: boolean
    onClose: () => void
    videonotes: VideoNotesAnalysisState | null
}

const CONSPECT_POINT = 5

const isPdf = (link = '', title = '') => /\.pdf(\?|$)/i.test(link) || /\.pdf$/i.test(title)

const ConspectReviewDialog: React.FC<Props> = ({ open, onClose, videonotes }) => {
    const { formatMessage, locale } = useIntl()
    const { readOnly } = useHeadScope()
    const [activeFile, setActiveFile] = useState(0)
    const [comment, setComment] = useState('')
    const [criteria, setCriteria] = useState([false, false, false])
    const [gradeVideonotes, { isLoading }] = videonotesApiSlice.useGradeVideonotesMutation()

    useEffect(() => {
        setComment(videonotes?.feedback ?? '')
        setActiveFile(0)
        setCriteria([false, false, false])
    }, [videonotes])

    if (!videonotes) return null

    const files = videonotes.files || []
    const file = files[activeFile]
    const graded = videonotes.point != null

    const accept = async () => {
        try {
            await gradeVideonotes({
                body: { videoNotesId: videonotes.id, point: CONSPECT_POINT, feedback: comment },
            }).unwrap()
            toast.success(formatMessage({ id: 'saved' }))
            onClose()
        } catch {}
    }

    const reject = async () => {
        try {
            await gradeVideonotes({
                body: { videoNotesId: videonotes.id, point: 0, feedback: comment },
            }).unwrap()
            toast.success(formatMessage({ id: 'saved' }))
            onClose()
        } catch {}
    }

    const criteriaLabels = ['conspectCriteria1', 'conspectCriteria2', 'conspectCriteria3']

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth PaperProps={{ sx: styles.paper }}>
            {/* header */}
            <Box sx={styles.header}>
                <Box sx={{ minWidth: 0 }}>
                    <Typography sx={styles.title}>
                        {videonotes[`topic${capitalize(locale)}` as keyof VideoNotesAnalysisState] as string}
                    </Typography>
                    <Typography sx={styles.subtitle}>{videonotes.videoLessonTitle}</Typography>
                </Box>
                <IconButton onClick={onClose}>
                    <CloseRounded />
                </IconButton>
            </Box>

            <Box sx={styles.body}>
                {/* viewer */}
                <Box sx={styles.viewer}>
                    <Box sx={styles.viewerBar}>
                        <Box sx={{ display: 'flex', gap: 0.8, alignItems: 'center', minWidth: 0, flex: 1 }}>
                            {file && (isPdf(file.link, file.title) ? (
                                <PictureAsPdfRounded sx={{ color: ui.pink, fontSize: 20 }} />
                            ) : (
                                <ImageRounded sx={{ color: ui.pink, fontSize: 20 }} />
                            ))}
                            <Typography sx={styles.fileName}>{file?.title || `file ${activeFile + 1}`}</Typography>
                        </Box>
                        {file && (
                            <Button
                                size="small"
                                startIcon={<DownloadRounded />}
                                onClick={() => window.open(file.link, '_blank')}
                                sx={styles.downloadBtn}
                            >
                                {formatMessage({ id: 'downloadFile' })}
                            </Button>
                        )}
                    </Box>

                    <Box sx={styles.viewerCanvas}>
                        {!file ? (
                            <Typography sx={styles.empty}>{formatMessage({ id: 'noVideonotes' })}</Typography>
                        ) : isPdf(file.link, file.title) ? (
                            <iframe
                                title={file.title || 'pdf'}
                                src={`${file.link}#toolbar=1&navpanes=0&view=FitH`}
                                style={iframeStyle}
                            />
                        ) : (
                            <img src={file.link} alt={file.title || 'conspect'} style={imgStyle} />
                        )}
                    </Box>

                    {files.length > 1 && (
                        <Box sx={styles.thumbs}>
                            {files.map((f, i) => (
                                <Box
                                    key={f.id ?? i}
                                    onClick={() => setActiveFile(i)}
                                    sx={{ ...styles.thumb, ...(i === activeFile ? styles.thumbActive : {}) }}
                                >
                                    {isPdf(f.link, f.title) ? (
                                        <PictureAsPdfRounded sx={{ fontSize: 16, color: ui.pink }} />
                                    ) : (
                                        <ImageRounded sx={{ fontSize: 16, color: ui.pink }} />
                                    )}
                                    {i + 1}
                                </Box>
                            ))}
                        </Box>
                    )}
                </Box>

                {/* evaluation */}
                <Box sx={styles.panel}>
                    <Typography sx={styles.panelTitle}>{formatMessage({ id: 'evaluation' })}</Typography>
                    <Typography sx={styles.hint}>{formatMessage({ id: 'conspectHint' })}</Typography>

                    <Box sx={{ mt: 1.5 }}>
                        {criteriaLabels.map((key, i) => (
                            <FormControlLabel
                                key={key}
                                sx={styles.check}
                                control={
                                    <Checkbox
                                        disabled={readOnly}
                                        checked={criteria[i]}
                                        onChange={() =>
                                            setCriteria((prev) => prev.map((v, idx) => (idx === i ? !v : v)))
                                        }
                                    />
                                }
                                label={<Typography sx={styles.checkLabel}>{formatMessage({ id: key })}</Typography>}
                            />
                        ))}
                    </Box>

                    <Typography sx={{ ...styles.panelTitle, fontSize: 14, mt: 2 }}>
                        {formatMessage({ id: 'mentorFeedback' })}
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        minRows={4}
                        value={comment}
                        disabled={readOnly}
                        onChange={(e) => setComment(e.target.value)}
                        placeholder={formatMessage({ id: 'mentorCommentPlaceholder' })}
                        sx={{ mt: 1 }}
                    />

                    <Box sx={{ flex: 1 }} />

                    {graded ? (
                        <Box sx={styles.gradedNote}>
                            {formatMessage({ id: 'points' })}: <b>{videonotes.point}</b>
                        </Box>
                    ) : null}

                    {!readOnly && <Box sx={{ display: 'flex', gap: 1.2, mt: 1.5 }}>
                        <Button variant="outlined" color="inherit" disabled={isLoading} onClick={reject} sx={styles.rejectBtn}>
                            {formatMessage({ id: 'reject' })}
                        </Button>
                        <Button variant="contained" disabled={isLoading} onClick={accept} sx={styles.acceptBtn}>
                            {formatMessage({ id: 'acceptPlusPoints' }, { points: CONSPECT_POINT })}
                        </Button>
                    </Box>}
                </Box>
            </Box>
        </Dialog>
    )
}

export default ConspectReviewDialog

const iframeStyle: React.CSSProperties = { width: '100%', height: '100%', border: 'none', background: ui.card }
const imgStyle: React.CSSProperties = { maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }

const styles: Record<string, any> = {
    paper: { borderRadius: '16px', height: '86vh', maxHeight: '860px' },
    header: {
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2,
        p: 2, borderBottom: `1px solid ${ui.border}`,
    },
    title: { fontSize: 17, fontWeight: 700, color: ui.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
    subtitle: { fontSize: 13, color: ui.muted },
    body: { display: 'flex', flex: 1, minHeight: 0, '@media (max-width: 900px)': { flexDirection: 'column' } },
    viewer: { flex: 1.6, minWidth: 0, display: 'flex', flexDirection: 'column', background: ui.ink, p: 1.5 },
    viewerBar: { display: 'flex', alignItems: 'center', gap: 1, mb: 1 },
    fileName: { fontSize: 13, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
    downloadBtn: { color: '#fff', textTransform: 'none', background: 'rgba(255,255,255,.14)', borderRadius: '8px', flexShrink: 0 },
    viewerCanvas: {
        flex: 1, minHeight: 0, borderRadius: '10px', overflow: 'hidden', background: ui.card,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
    },
    empty: { color: ui.muted, fontSize: 14, p: 4 },
    thumbs: { display: 'flex', gap: 0.8, mt: 1, flexWrap: 'wrap' },
    thumb: {
        display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.5, borderRadius: '8px',
        background: 'rgba(255,255,255,.14)', color: '#fff', fontSize: 12, cursor: 'pointer',
    },
    thumbActive: { background: ui.card, color: ui.ink },
    panel: { flex: 1, minWidth: 320, display: 'flex', flexDirection: 'column', p: 2.5, '@media (max-width: 900px)': { minWidth: 0 } },
    panelTitle: { fontSize: 16, fontWeight: 700, color: ui.ink },
    hint: { fontSize: 12.5, color: ui.muted, mt: 0.5 },
    check: { alignItems: 'flex-start', mb: 0.5 },
    checkLabel: { fontSize: 13.5, color: ui.ink, mt: 0.6 },
    gradedNote: { fontSize: 14, color: ui.ink },
    rejectBtn: { textTransform: 'none', borderRadius: '10px', color: ui.muted, borderColor: ui.border },
    acceptBtn: { flex: 1, textTransform: 'none', borderRadius: '10px', py: 1.1, background: ui.pink, '&:hover': { background: ui.pinkDark } },
}
